/**
 * Elation Universal Product
 * Handles product switching, bundle selection, Family Pack builder, and cart operations
 */

import { CartAddEvent } from '@theme/events';

class ElationUniversalProduct {
  constructor() {
    this.products = [];
    this.currentProduct = null;
    this.selectedVariant = null;
    this.mixMatchItems = [];
    this.quantity = 1;

    // Bundle tier settings (dynamic bundles)
    this.selectedBundleTier = 1;
    this.selectedBundleQuantity = 1;
    this.selectedBundleDiscount = 0;

    // Bundle discount settings
    this.bundle2Discount = 10;
    this.bundle4Discount = 25;

    this.init();
  }

  init() {
    this.loadProducts();
    this.handleURLParams();
    this.initializeDefaultProduct();
    this.bindEvents();

    // Sync bundle state to the pre-checked radio on page load
    const checkedBundle = document.querySelector('[data-bundle-tier]:checked');
    if (checkedBundle) {
      this.selectBundle(
        parseInt(checkedBundle.dataset.bundleTier),
        parseInt(checkedBundle.dataset.bundleQuantity),
        parseInt(checkedBundle.dataset.bundleDiscount)
      );
    }
  }

  /**
   * Load product data from JSON
   */
  loadProducts() {
    const productDataEl = document.querySelector('[data-product-json]');

    if (productDataEl) {
      try {
        const data = JSON.parse(productDataEl.textContent);
        this.products = data.products || [];

        if (this.products.length > 0 && !this.currentProduct) {
          this.currentProduct = this.products[0];
        }
      } catch (error) {
        console.error('Error loading product data:', error);
      }
    }
  }

  /**
   * Handle URL parameters for product selection
   */
  handleURLParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const productParam = urlParams.get('product');

    if (productParam && this.products.length > 0) {
      const productMap = {
        'morning': this.products.find(p => p.category === 'adult' && p.shortName === 'Morning'),
        'moon': this.products.find(p => p.category === 'adult' && p.shortName === 'Moon'),
        'berry-melon': this.products.find(p => p.category === 'kids' && p.shortName === 'Berry Melon'),
        'midnight': this.products.find(p => p.category === 'kids' && p.shortName === 'Midnight')
      };

      const product = productMap[productParam.toLowerCase()] ||
                      this.products.find(p => p.handle === productParam);

      if (product) {
        this.selectProduct(product.handle, false);
      }
    }
  }

  /**
   * Initialize default product and variant if not already set
   */
  initializeDefaultProduct() {
    if (!this.currentProduct && this.products.length > 0) {
      this.currentProduct = this.products[0];
    }

    if (this.currentProduct && !this.selectedVariant) {
      // Select first available variant
      if (this.currentProduct.variants && this.currentProduct.variants.length > 0) {
        const firstVariant = this.currentProduct.variants.find(v => v.available) || this.currentProduct.variants[0];
        this.selectedVariant = firstVariant;

        // Update variant ID in form
        const variantInput = document.querySelector('[data-variant-id]');
        if (variantInput && firstVariant) {
          variantInput.value = firstVariant.id;
        }

        // Update pricing
        this.updatePricing();
      }
    }
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Product selectors
    document.querySelectorAll('[data-product-selector]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const handle = btn.dataset.productHandle;
        this.selectProduct(handle, true);
      });
    });

    // Bundle selectors (dynamic tiers)
    document.querySelectorAll('[data-bundle-tier]').forEach(input => {
      input.addEventListener('change', (e) => {
        const tier = parseInt(e.target.dataset.bundleTier);
        const quantity = parseInt(e.target.dataset.bundleQuantity);
        const discount = parseInt(e.target.dataset.bundleDiscount);
        this.selectBundle(tier, quantity, discount);
      });
    });

    // Quantity controls
    const qtyInput = document.querySelector('[data-quantity-input]');
    const qtyMinus = document.querySelector('[data-quantity-minus]');
    const qtyPlus = document.querySelector('[data-quantity-plus]');

    if (qtyMinus) {
      qtyMinus.addEventListener('click', () => this.decreaseQuantity());
    }

    if (qtyPlus) {
      qtyPlus.addEventListener('click', () => this.increaseQuantity());
    }

    if (qtyInput) {
      qtyInput.addEventListener('change', (e) => {
        this.quantity = parseInt(e.target.value) || 1;
        this.autoSelectBundleByQuantity();
        this.updatePricing();
      });
    }

    // Mix & Match builder (unified for 2 Jars & Family Pack)
    document.querySelectorAll('[data-mix-match-minus]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const productId = btn.dataset.productId;
        this.updateMixMatchQuantity(productId, -1);
      });
    });

    document.querySelectorAll('[data-mix-match-plus]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const productId = btn.dataset.productId;
        this.updateMixMatchQuantity(productId, 1);
      });
    });

    // Image thumbnails
    document.querySelectorAll('[data-thumbnail-btn]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const imageUrl = btn.dataset.imageUrl;
        this.switchToImage(imageUrl);

        // Update active state
        document.querySelectorAll('[data-thumbnail-btn]').forEach(tb => {
          tb.classList.remove('elation-universal-product__thumbnail--active');
        });
        btn.classList.add('elation-universal-product__thumbnail--active');
        this.updateCarouselArrows();
      });
    });

    // Carousel arrow buttons
    const prevBtn = document.querySelector('[data-carousel-prev]');
    const nextBtn = document.querySelector('[data-carousel-next]');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        const active = document.querySelector('.elation-universal-product__thumbnail--active');
        const prev = active ? active.previousElementSibling : null;

        if (prev && prev.dataset.thumbnailBtn !== undefined) {
          prev.click();
        }
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        const active = document.querySelector('.elation-universal-product__thumbnail--active');
        const next = active ? active.nextElementSibling : null;

        if (next && next.dataset.thumbnailBtn !== undefined) {
          next.click();
        }
      });
    }

    this.updateCarouselArrows();

    // Description toggle
    const descToggle = document.querySelector('[data-description-toggle]');
    if (descToggle) {
      descToggle.addEventListener('click', () => {
        this.toggleDescription();
      });
    }

    // Form submission
    const form = document.getElementById('product-form');
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }

    // Subscription selector
    document.querySelectorAll('[data-purchase-option]').forEach(radio => {
      radio.addEventListener('change', () => {
        this.updatePricing();
      });
    });

    const sellingPlanSelect = document.querySelector('[data-selling-plan-select]');
    if (sellingPlanSelect) {
      sellingPlanSelect.addEventListener('change', () => {
        this.updatePricing();
      });
    }
  }

  /**
   * Select a product by handle
   */
  selectProduct(handle, updateURL = true) {
    const product = this.products.find(p => p.handle === handle);

    if (!product) {
      console.error('Product not found:', handle);
      return;
    }

    this.currentProduct = product;

    // Update product selector UI
    document.querySelectorAll('[data-product-selector]').forEach(btn => {
      const isSelected = btn.dataset.productHandle === handle;

      if (isSelected) {
        btn.classList.add('elation-product-selector__card--selected');

        // Add checkmark to slot if not present
        const checkmarkSlot = btn.querySelector('.elation-product-selector__checkmark-slot');
        if (checkmarkSlot && !checkmarkSlot.querySelector('.elation-product-selector__checkmark')) {
          const checkmark = document.createElement('div');
          checkmark.className = 'elation-product-selector__checkmark';
          checkmark.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="11" fill="#9FBF45"/>
              <path d="M7 12L10.5 15.5L17 9" stroke="white" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          `;
          checkmarkSlot.appendChild(checkmark);
        }
      } else {
        btn.classList.remove('elation-product-selector__card--selected');

        // Remove checkmark from slot if present
        const checkmarkSlot = btn.querySelector('.elation-product-selector__checkmark-slot');
        if (checkmarkSlot) {
          const checkmark = checkmarkSlot.querySelector('.elation-product-selector__checkmark');
          if (checkmark) {
            checkmark.remove();
          }
        }
      }
    });

    // Update product display
    this.updateProductDisplay();

    // Select first available variant
    if (product.variants && product.variants.length > 0) {
      const firstAvailableVariant = product.variants.find(v => v.available) || product.variants[0];
      this.selectVariant(firstAvailableVariant.id, false);
    }

    // Update URL
    if (updateURL && history.pushState) {
      const shortName = product.shortName.toLowerCase().replace(/\s+/g, '-');
      const newURL = `${window.location.pathname}?product=${shortName}`;
      history.pushState({ product: handle }, '', newURL);
    }
  }

  /**
   * Update product display with current product data
   */
  updateProductDisplay() {
    const product = this.currentProduct;
    if (!product) return;

    // Update title
    const titleEl = document.querySelector('[data-product-title]');
    if (titleEl) {
      titleEl.textContent = product.title;
      titleEl.classList.add('fade-in');
    }

    // Update subtitle
    const subtitleEl = document.querySelector('[data-product-subtitle]');
    if (subtitleEl && product.subtitle) {
      subtitleEl.textContent = product.subtitle;
    }

    // Update description (preserve HTML formatting)
    const descHtml = product.descriptionHtml || product.description || '';

    // Short preview: plain text, first 200 chars
    const tmpDiv = document.createElement('div');
    tmpDiv.innerHTML = descHtml;
    const plainText = (tmpDiv.textContent || '').replace(/\s+/g, ' ').trim();
    const descEl = document.querySelector('[data-description-short] p');
    if (descEl && plainText) {
      descEl.textContent = plainText.substring(0, 200) + (plainText.length > 200 ? '...' : '');
    }

    // Full description: render formatted HTML
    const descFullEl = document.querySelector('[data-description-full]');
    if (descFullEl && descHtml) {
      descFullEl.innerHTML = descHtml;
    }

    // Update claims
    const claimsEl = document.querySelector('[data-product-claims]');
    if (claimsEl && product.claims && Array.isArray(product.claims)) {
      claimsEl.innerHTML = '';
      product.claims.forEach(claim => {
        const claimDiv = document.createElement('div');
        claimDiv.className = 'elation-universal-product__claim';
        claimDiv.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="9" stroke="#228B22" stroke-width="2" fill="none"/>
            <path d="M6 10L9 13L14 7" stroke="#228B22" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>${claim}</span>
        `;
        claimsEl.appendChild(claimDiv);
      });
    }

    // Rating and review count are static (from section settings), no update needed

    // Update main image
    this.updateMainImage(0);

    // Update "What's Inside" section if it exists
    this.updateIngredientsSection();
  }

  /**
   * Update main product image
   */
  updateMainImage(index) {
    const product = this.currentProduct;
    if (!product || !product.images || !product.images[index]) return;

    const mainImageEl = document.querySelector('[data-main-image]');
    if (mainImageEl) {
      mainImageEl.src = product.images[index];
      mainImageEl.classList.remove('fade-in');
      void mainImageEl.offsetWidth; // Force reflow
      mainImageEl.classList.add('fade-in');
    }

    // Update thumbnails
    this.updateThumbnails();
  }

  /**
   * Update thumbnail images for current product
   */
  updateThumbnails() {
    const product = this.currentProduct;
    if (!product || !product.images) return;

    const thumbnailsContainer = document.querySelector('[data-product-thumbnails]');
    if (!thumbnailsContainer) return;

    // Only show thumbnails if product has multiple images
    if (product.images.length <= 1) {
      thumbnailsContainer.innerHTML = '';
      return;
    }

    thumbnailsContainer.innerHTML = '';
    product.images.forEach((imageUrl, index) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `elation-universal-product__thumbnail${index === 0 ? ' elation-universal-product__thumbnail--active' : ''}`;
      btn.setAttribute('data-thumbnail-btn', '');
      btn.setAttribute('data-image-url', imageUrl);
      btn.setAttribute('aria-label', `View image ${index + 1}`);

      const img = document.createElement('img');
      img.src = imageUrl.replace('width=800', 'width=150');
      img.alt = `Product image ${index + 1}`;
      img.width = 150;
      img.height = 150;
      img.loading = 'lazy';

      btn.appendChild(img);
      thumbnailsContainer.appendChild(btn);

      // Add click handler
      btn.addEventListener('click', () => {
        this.switchToImage(imageUrl);
        document.querySelectorAll('[data-thumbnail-btn]').forEach(b => {
          b.classList.remove('elation-universal-product__thumbnail--active');
        });
        btn.classList.add('elation-universal-product__thumbnail--active');
        this.updateCarouselArrows();
      });
    });

    this.updateCarouselArrows();
  }

  updateCarouselArrows() {
    const thumbs = document.querySelectorAll('[data-thumbnail-btn]');
    const active = document.querySelector('.elation-universal-product__thumbnail--active');
    const prevBtn = document.querySelector('[data-carousel-prev]');
    const nextBtn = document.querySelector('[data-carousel-next]');

    if (!active || !prevBtn || !nextBtn) return;

    const index = Array.from(thumbs).indexOf(active);
    prevBtn.disabled = index === 0;
    nextBtn.disabled = index === thumbs.length - 1;
  }

  /**
   * Switch to specific image index
   */
  switchImage(index) {
    this.updateMainImage(index);
  }

  /**
   * Switch to specific image URL
   */
  switchToImage(imageUrl) {
    const mainImageEl = document.querySelector('[data-main-image]');

    if (mainImageEl) {
      mainImageEl.src = imageUrl;
      mainImageEl.classList.remove('fade-in');
      void mainImageEl.offsetWidth;
      mainImageEl.classList.add('fade-in');
    }
  }

  /**
   * Update "What's Inside" ingredients section with current product data
   */
  updateIngredientsSection() {
    const product = this.currentProduct;
    if (!product || !product.ingredientsData) return;

    const nutrientsContainer = document.querySelector('.elation-whats-inside__nutrients');
    if (nutrientsContainer && product.ingredientsData.items) {
      nutrientsContainer.innerHTML = '';
      product.ingredientsData.items.forEach(item => {
        const nutrientDiv = document.createElement('div');
        nutrientDiv.className = 'elation-whats-inside__nutrient';

        const amountParts = item.amount ? item.amount.split(' ') : ['', ''];
        const amount = amountParts[0] || '';
        const unit = amountParts[1] || '';

        nutrientDiv.innerHTML = `
          <div class="elation-whats-inside__nutrient-label">${item.name ? item.name.toUpperCase() : ''}</div>
          <div class="elation-whats-inside__nutrient-amount">
            ${amount}<span class="elation-whats-inside__nutrient-unit">${unit}</span>
          </div>
          ${item.form ? `<div class="elation-whats-inside__nutrient-form">${item.form}</div>` : ''}
        `;
        nutrientsContainer.appendChild(nutrientDiv);
      });
    }

    // Update other ingredients text
    const otherIngredientsText = document.querySelector('.elation-whats-inside__other-text');
    if (otherIngredientsText && product.ingredientsData.other_ingredients) {
      otherIngredientsText.textContent = product.ingredientsData.other_ingredients;
    }
  }

  /**
   * Select bundle tier (dynamic bundles)
   */
  selectBundle(tier, quantity, discount) {
    this.selectedBundleTier = tier;
    this.selectedBundleQuantity = quantity;
    this.selectedBundleDiscount = discount;

    // Store discount settings from DOM
    const bundle2El = document.querySelector('[data-bundle-tier="2"]');
    const bundle4El = document.querySelector('[data-bundle-tier="4"]');
    if (bundle2El) this.bundle2Discount = parseInt(bundle2El.dataset.bundleDiscount) || 10;
    if (bundle4El) this.bundle4Discount = parseInt(bundle4El.dataset.bundleDiscount) || 25;

    // Show/hide Mix & Match builder for tiers 2 and 4
    const mixMatchBuilder = document.querySelector('[data-mix-match-builder]');

    if (tier === 2 || tier === 4) {
      // Show unified Mix & Match builder
      if (mixMatchBuilder) {
        mixMatchBuilder.hidden = false;
        this.initializeMixMatch(tier);
      }
    } else {
      // Hide builder for tier 1
      if (mixMatchBuilder) {
        mixMatchBuilder.hidden = true;
      }

      // Update quantity input for tier 1
      const qtyInput = document.querySelector('[data-quantity-input]');
      if (qtyInput) {
        qtyInput.value = quantity;
        this.quantity = quantity;
      }
    }

    this.updatePricing();
  }

  /**
   * Select a variant by ID
   */
  selectVariant(variantId, updateUI = true) {
    if (!this.currentProduct) return;

    const variant = this.currentProduct.variants.find(v => v.id == variantId);
    if (!variant) return;

    this.selectedVariant = variant;

    // Update variant ID in form
    const variantInput = document.querySelector('[data-variant-id]');
    if (variantInput) {
      variantInput.value = variant.id;
    }

    this.updatePricing();
  }

  /**
   * Initialize Mix & Match builder with default selection
   */
  initializeMixMatch(tier) {
    this.mixMatchItems = [];

    const elements = document.querySelectorAll('[data-mix-match-quantity]');

    if (tier === 4) {
      // Family Pack: Set 1 of each product (up to 4 products)
      elements.forEach((element, index) => {
        const productCard = element.closest('[data-mix-match-product]');

        if (index < 4) {
          element.textContent = 1;
          if (productCard) productCard.setAttribute('data-selected', 'true');
        } else {
          element.textContent = 0;
          if (productCard) productCard.removeAttribute('data-selected');
        }
      });
    } else {
      // 2 Jars: Set 1 of each for first 2 products
      elements.forEach((element, index) => {
        const productCard = element.closest('[data-mix-match-product]');

        if (index < 2) {
          element.textContent = 1;
          if (productCard) productCard.setAttribute('data-selected', 'true');
        } else {
          element.textContent = 0;
          if (productCard) productCard.removeAttribute('data-selected');
        }
      });
    }

    this.validateMixMatch();
  }

  /**
   * Update Mix & Match quantity for a product
   */
  updateMixMatchQuantity(productId, change) {
    const qtyElement = document.querySelector(`[data-mix-match-quantity][data-product-id="${productId}"]`);
    if (!qtyElement) return;

    let currentQty = parseInt(qtyElement.textContent) || 0;
    let newQty = Math.max(0, currentQty + change);

    qtyElement.textContent = newQty;

    // Update visual state
    const productCard = qtyElement.closest('[data-mix-match-product]');
    if (productCard) {
      if (newQty > 0) {
        productCard.setAttribute('data-selected', 'true');
      } else {
        productCard.removeAttribute('data-selected');
      }
    }

    this.validateMixMatch();
  }

  /**
   * Get total jars in Mix & Match bundle
   */
  getMixMatchTotal() {
    let total = 0;
    document.querySelectorAll('[data-mix-match-quantity]').forEach(element => {
      total += parseInt(element.textContent) || 0;
    });
    return total;
  }

  /**
   * Validate Mix & Match selection and auto-switch tiers
   */
  validateMixMatch() {
    const total = this.getMixMatchTotal();
    const totalEl = document.querySelector('[data-mix-match-total]');
    const validationEl = document.querySelector('[data-mix-match-validation]');
    const discountInfoEl = document.querySelector('[data-mix-match-discount-info]');
    const discountTextEl = document.querySelector('[data-mix-match-discount-text]');

    // Update total display
    if (totalEl) {
      totalEl.textContent = total;
    }

    // Update Mix & Match product prices based on total quantity
    this.updateMixMatchPrices(total);

    // Auto-switch bundle tier based on quantity
    if (total >= 4) {
      // Switch to Family Pack (tier 4)
      if (this.selectedBundleTier !== 4) {
        this.selectedBundleTier = 4;
        this.selectedBundleDiscount = this.bundle4Discount;

        // Update UI - check Family Pack radio
        const familyPackRadio = document.querySelector('[data-bundle-tier="4"]');
        if (familyPackRadio) {
          familyPackRadio.checked = true;
        }
      }

      // Hide discount hint
      if (discountInfoEl) discountInfoEl.hidden = true;

      // Update pricing on every quantity change
      this.updatePricing();
    } else if (total >= 2) {
      // Switch to 2 Jars (tier 2)
      if (this.selectedBundleTier !== 2) {
        this.selectedBundleTier = 2;
        this.selectedBundleDiscount = this.bundle2Discount;

        // Update UI - check 2 Jars radio
        const twoJarRadio = document.querySelector('[data-bundle-tier="2"]');
        if (twoJarRadio) {
          twoJarRadio.checked = true;
        }
      }

      // Show discount hint if 3 jars selected
      if (total === 3) {
        if (discountInfoEl) discountInfoEl.hidden = false;
        if (discountTextEl) discountTextEl.textContent = `Add 1 more jar to unlock Family Pack discount (${this.bundle4Discount}% off)!`;
      } else {
        if (discountInfoEl) discountInfoEl.hidden = true;
      }

      // Update pricing on every quantity change
      this.updatePricing();
    }

    // Validation message
    if (total >= 2) {
      if (validationEl) validationEl.hidden = true;
      return true;
    } else {
      if (validationEl) {
        validationEl.hidden = false;
        const validationMsg = document.querySelector('[data-mix-match-validation-message]');
        if (validationMsg) {
          const needed = 2 - total;
          validationMsg.textContent = total === 0
            ? 'Please select at least 2 jars'
            : `Add ${needed} more jar${needed > 1 ? 's' : ''} (minimum 2 jars)`;
        }
      }
      if (discountInfoEl) discountInfoEl.hidden = true;

      // FIX: Recalculate prices when bundle becomes invalid
      this.updatePricing();

      return false;
    }
  }

  /**
   * Update Mix & Match product prices based on total quantity
   */
  updateMixMatchPrices(total) {
    let discount = 0;

    if (total >= 4) {
      discount = this.bundle4Discount; // 25% off
    } else if (total >= 2) {
      discount = this.bundle2Discount; // 10% off
    }

    // Update each product's displayed price
    document.querySelectorAll('[data-mix-match-price]').forEach(priceEl => {
      const basePrice = parseInt(priceEl.dataset.basePrice) || 0;
      const discountLabel = priceEl.parentElement.querySelector('[data-mix-match-discount-label]');

      if (discount > 0) {
        // Calculate discounted price
        const discountedPrice = Math.round(basePrice * (100 - discount) / 100);

        // Update main price to show discounted amount
        priceEl.textContent = this.formatMoney(discountedPrice);

        // Show discount label with original price and savings
        if (discountLabel) {
          const originalPriceEl = discountLabel.querySelector('.elation-two-jar-builder__price-original');
          const saveEl = discountLabel.querySelector('.elation-two-jar-builder__price-save');

          if (originalPriceEl) {
            originalPriceEl.textContent = this.formatMoney(basePrice);
          }
          if (saveEl) {
            saveEl.textContent = `Save ${discount}%`;
          }

          discountLabel.hidden = false;
        }
      } else {
        // No discount - show base price
        priceEl.textContent = this.formatMoney(basePrice);

        // Hide discount label
        if (discountLabel) {
          discountLabel.hidden = true;
        }
      }
    });
  }

  /**
   * Increase quantity
   */
  increaseQuantity() {
    this.quantity++;
    const input = document.querySelector('[data-quantity-input]');
    if (input) {
      input.value = this.quantity;
    }
    this.autoSelectBundleByQuantity();
    this.updatePricing();
  }

  /**
   * Decrease quantity
   */
  decreaseQuantity() {
    if (this.quantity > 1) {
      this.quantity--;
      const input = document.querySelector('[data-quantity-input]');
      if (input) {
        input.value = this.quantity;
      }
      this.autoSelectBundleByQuantity();
      this.updatePricing();
    }
  }

  /**
   * Auto-select bundle tier based on quantity
   */
  autoSelectBundleByQuantity() {
    let bundleTier = 1;
    let discount = 0;

    // Get discount settings from section
    const bundle2Discount = parseInt(document.querySelector('[data-bundle-tier="2"]')?.dataset.bundleDiscount) || 10;
    const bundle4Discount = parseInt(document.querySelector('[data-bundle-tier="4"]')?.dataset.bundleDiscount) || 25;

    if (this.quantity >= 4) {
      bundleTier = 4;
      discount = bundle4Discount;
    } else if (this.quantity >= 2) {
      bundleTier = 2;
      discount = bundle2Discount;
    }

    // Update selected bundle tier
    this.selectedBundleTier = bundleTier;
    this.selectedBundleQuantity = bundleTier === 4 ? 4 : bundleTier;
    this.selectedBundleDiscount = discount;

    // Update bundle selector UI
    document.querySelectorAll('[data-bundle-tier]').forEach(input => {
      const tier = parseInt(input.dataset.bundleTier);
      input.checked = (tier === bundleTier);
    });
  }

  /**
   * Update pricing display
   */
  updatePricing() {
    if (!this.selectedVariant) return;

    const basePrice = this.selectedVariant.price;
    let total;
    let originalTotal;
    let hasDiscount = false;

    // Calculate based on bundle tier
    if (this.selectedBundleTier && this.selectedBundleTier !== 4 && this.selectedBundleTier !== 2) {
      // Calculate original price (without discount)
      // Quantity is always the number of individual jars, not bundles
      originalTotal = basePrice * this.quantity;

      // Apply discount based on bundle tier
      const discountMultiplier = (100 - this.selectedBundleDiscount) / 100;
      total = originalTotal * discountMultiplier;

      hasDiscount = this.selectedBundleDiscount > 0;
    } else if (this.selectedBundleTier === 2 || this.selectedBundleTier === 4) {
      // Mix & Match bundle - use builder total
      const mixMatchTotal = this.getMixMatchTotal();

      if (mixMatchTotal >= 2) {
        // Apply discount (valid bundle with 2+ jars)
        originalTotal = basePrice * mixMatchTotal;
        const discountMultiplier = (100 - this.selectedBundleDiscount) / 100;
        total = originalTotal * discountMultiplier;
        hasDiscount = this.selectedBundleDiscount > 0;
      } else if (mixMatchTotal > 0) {
        // No discount when < 2 jars (below minimum)
        total = basePrice * mixMatchTotal;
        originalTotal = total;
        hasDiscount = false;
      } else {
        // Fallback if no products selected yet
        const defaultQty = this.selectedBundleTier === 4 ? 4 : 2;
        total = basePrice * defaultQty;
        originalTotal = total;
      }
    } else {
      // Fallback to quantity
      total = basePrice * this.quantity;
      originalTotal = total;
    }

    // Update one-time price (for subscription selector)
    const onetimePriceEl = document.querySelector('[data-onetime-price]');
    if (onetimePriceEl) {
      onetimePriceEl.textContent = this.formatMoney(total);
    }

    // Trigger subscription price update
    this.updateSubscriptionPrice();

    // Update button price with discount display
    const buttonPriceEl = document.querySelector('[data-button-price]');
    if (buttonPriceEl) {
      if (hasDiscount) {
        buttonPriceEl.innerHTML = `
          <span style="text-decoration: line-through; opacity: 0.6; font-size: 0.875em; margin-right: 0.5rem;">${this.formatMoney(originalTotal)}</span>
          <span>${this.formatMoney(total)}</span>
        `;
      } else {
        buttonPriceEl.textContent = this.formatMoney(total);
      }
    }

    // Update total price in add to cart button (legacy)
    const totalPriceEl = document.querySelector('[data-total-price]');
    if (totalPriceEl) {
      totalPriceEl.textContent = this.formatMoney(total);
    }

    // Update single price display (legacy)
    const priceEl = document.querySelector('[data-product-price] .elation-universal-product__price-amount');
    if (priceEl) {
      priceEl.textContent = this.formatMoney(basePrice);
    }
  }

  /**
   * Update subscription price display
   */
  updateSubscriptionPrice() {
    const sellingPlanSelect = document.querySelector('[data-selling-plan-select]');
    const subscribePriceEl = document.querySelector('[data-subscribe-price]');
    const onetimePriceEl = document.querySelector('[data-onetime-price]');

    if (!sellingPlanSelect || !subscribePriceEl) return;

    // Get selected selling plan ID
    const selectedSellingPlanId = sellingPlanSelect.value;
    if (!selectedSellingPlanId) return;

    // Get current variant
    if (!this.selectedVariant || !this.selectedVariant.sellingPlanAllocations) return;

    // Find the selling plan allocation for the selected plan
    const allocation = this.selectedVariant.sellingPlanAllocations.find(
      alloc => alloc.selling_plan_id == selectedSellingPlanId
    );

    if (allocation && allocation.price) {
      // For bundles (tier 2 or 4), calculate total subscription price
      if (this.selectedBundleTier === 2 || this.selectedBundleTier === 4) {
        const mixMatchTotal = this.getMixMatchTotal();
        if (mixMatchTotal > 0) {
          // Total subscription price = allocation price × quantity
          // NO bundle discount applied - subscription uses selling plan pricing only
          const totalSubscriptionPrice = allocation.price * mixMatchTotal;
          subscribePriceEl.textContent = this.formatMoney(totalSubscriptionPrice);
        } else {
          subscribePriceEl.textContent = this.formatMoney(allocation.price);
        }
      } else {
        // Single jar - use allocation price as-is
        subscribePriceEl.textContent = this.formatMoney(allocation.price * this.quantity);
      }
    } else if (onetimePriceEl) {
      // Fallback to one-time price
      subscribePriceEl.textContent = onetimePriceEl.textContent;
    }
  }

  /**
   * Format money (basic implementation)
   */
  formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  /**
   * Toggle description
   */
  toggleDescription() {
    const shortDesc = document.querySelector('[data-description-short]');
    const fullDesc = document.querySelector('[data-description-full]');
    const toggle = document.querySelector('[data-description-toggle]');

    if (shortDesc && fullDesc && toggle) {
      if (fullDesc.hidden) {
        // Show full, hide short
        shortDesc.hidden = true;
        fullDesc.hidden = false;
        toggle.textContent = 'Read less';
      } else {
        // Show short, hide full
        shortDesc.hidden = false;
        fullDesc.hidden = true;
        toggle.textContent = 'Read more';
      }
    }
  }

  /**
   * Add to cart
   */
  async addToCart() {
    // Check if Mix & Match bundle (tiers 2 or 4)
    if (this.selectedBundleTier === 2 || this.selectedBundleTier === 4) {
      // Validate Mix & Match
      if (!this.validateMixMatch()) {
        this.showError('Please select at least 2 jars');
        return;
      }

      // Add mix & match bundle
      await this.addMixMatchToCart();
      return;
    }

    // Otherwise, add single variant
    if (!this.selectedVariant) {
      this.showError('Please select a product');
      return;
    }

    await this.addSingleItemToCart();
  }

  /**
   * Add single item to cart
   */
  async addSingleItemToCart() {
    const item = {
      id: this.selectedVariant.id,
      quantity: this.quantity
    };

    // Check if subscription is selected
    const subscriptionRadio = document.querySelector('[data-purchase-option][value="subscribe"]');
    if (subscriptionRadio && subscriptionRadio.checked) {
      const sellingPlanSelect = document.querySelector('[data-selling-plan-select]');
      if (sellingPlanSelect && sellingPlanSelect.value) {
        item.selling_plan = sellingPlanSelect.value;
      }
    }

    const formData = {
      items: [item]
    };

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        this.showSuccess('Added to cart!');
        await this.refreshCart();
      } else {
        if (response.status === 429) {
          throw new Error('Too many requests. Please wait a moment and try again.');
        }

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          console.error('Cart add failed:', errorData);
          throw new Error(errorData.description || 'Failed to add to cart');
        } else {
          throw new Error('Failed to add to cart');
        }
      }
    } catch (error) {
      console.error('Cart error:', error);
      this.showError(error.message || 'Failed to add to cart. Please try again.');
    }
  }

  /**
   * Add Mix & Match bundle to cart (unified for 2 Jars & Family Pack)
   */
  async addMixMatchToCart() {
    const total = this.getMixMatchTotal();
    const bundleType = total >= 4 ? 'Family Pack' : '2 Jar Mix & Match';
    const bundleGroupId = `mix-match-${Date.now()}`;
    const items = [];

    // Check if subscription is selected
    const subscriptionRadio = document.querySelector('[data-purchase-option][value="subscribe"]');
    const isSubscription = subscriptionRadio && subscriptionRadio.checked;
    let sellingPlanId = null;

    if (isSubscription) {
      const sellingPlanSelect = document.querySelector('[data-selling-plan-select]');
      if (sellingPlanSelect && sellingPlanSelect.value) {
        sellingPlanId = sellingPlanSelect.value;
      }
    }

    document.querySelectorAll('[data-mix-match-quantity]').forEach(element => {
      const quantity = parseInt(element.textContent) || 0;
      if (quantity > 0) {
        const productHandle = element.dataset.productHandle;
        const product = this.products.find(p => p.handle === productHandle);

        if (product && product.variants && product.variants.length > 0) {
          // Use first variant from each product
          const variant = product.variants[0];

          const item = {
            id: variant.id,
            quantity: quantity,
            properties: {
              '_bundle_type': bundleType,
              '_bundle_group_id': bundleGroupId,
              '_bundle_discount': `${this.selectedBundleDiscount}%`
            }
          };

          // Add selling plan if subscription selected
          if (sellingPlanId) {
            item.selling_plan = sellingPlanId;
          }

          items.push(item);
        }
      }
    });

    if (items.length === 0) {
      this.showError('No products selected');
      return;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      if (response.ok) {
        this.showSuccess(`${bundleType} added to cart!`);
        await this.refreshCart();
      } else {
        if (response.status === 429) {
          throw new Error('Too many requests. Please wait a moment and try again.');
        }

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          console.error('Cart add failed:', errorData);
          throw new Error(errorData.description || `Failed to add ${bundleType} to cart`);
        } else {
          throw new Error(`Failed to add ${bundleType} to cart`);
        }
      }
    } catch (error) {
      console.error('Cart error:', error);
      this.showError(error.message || `Failed to add ${bundleType} to cart. Please try again.`);
    }
  }

  /**
   * Refresh cart (trigger cart drawer to open)
   */
  async refreshCart() {
    try {
      // Fetch updated cart state
      const response = await fetch('/cart.js');

      if (!response.ok) {
        console.warn('Failed to fetch cart, skipping drawer open');
        return;
      }

      const cart = await response.json();

      // Dispatch CartAddEvent to trigger cart drawer
      const event = new CartAddEvent(cart, null, {
        itemCount: cart.item_count
      });
      document.dispatchEvent(event);
    } catch (error) {
      console.error('Failed to refresh cart:', error);
    }
  }

  /**
   * Show success message
   */
  showSuccess(message) {
    this.showToast(message, 'success');
  }

  /**
   * Show error message
   */
  showError(message) {
    this.showToast(message, 'error');
  }

  /**
   * Show toast notification
   */
  showToast(message, type = 'success') {
    // Remove any existing toasts
    const existingToast = document.querySelector('.elation-toast');
    if (existingToast) {
      existingToast.remove();
    }

    // Create toast element
    const toast = document.createElement('div');
    toast.className = `elation-toast elation-toast--${type}`;
    toast.innerHTML = `
      <div class="elation-toast__content">
        <svg class="elation-toast__icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
          ${type === 'success'
            ? '<circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="2"/><path d="M6 10L9 13L14 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>'
            : '<circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="2"/><path d="M10 6V10M10 13V14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>'}
        </svg>
        <span class="elation-toast__message">${message}</span>
      </div>
      <button class="elation-toast__close" aria-label="Close">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
    `;

    // Add to page
    document.body.appendChild(toast);

    // Close button handler
    const closeBtn = toast.querySelector('.elation-toast__close');
    closeBtn.addEventListener('click', () => {
      toast.classList.add('elation-toast--hiding');
      setTimeout(() => toast.remove(), 300);
    });

    // Show toast with animation
    requestAnimationFrame(() => {
      toast.classList.add('elation-toast--show');
    });

    // Auto-dismiss after 4 seconds
    setTimeout(() => {
      toast.classList.add('elation-toast--hiding');
      setTimeout(() => toast.remove(), 300);
    }, 4000);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ElationUniversalProduct();
  });
} else {
  new ElationUniversalProduct();
}
